import React, { useState } from "react";

/* Human-readable editor for the GS-direct knob document. Every field writes
   into the same JSON the derivation consumes — the raw document remains
   available under "Advanced", but nobody should need it day-to-day. */

const deep = (o) => JSON.parse(JSON.stringify(o));

// Strip binary floating-point noise for DISPLAY only (e.g. 0.30000000000000004
// -> "0.3", 0.8550000000000018 -> "0.855"). Pricing knobs never carry more than
// a few real decimals, so rounding to 6 separates signal from noise; trailing
// zeros are dropped by the Number() round-trip. Non-numeric/blank pass through.
const fmtNum = (v) => {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v !== "number" || !isFinite(v)) return String(v);
  return String(Number(v.toFixed(6)));
};

const FLAG_LABELS = {
  inertPriceCap400:
    "Keep the workbook's inactive price cap (it points at the $400 review-fee cell — a known quirk; unticking makes the 106.5 max price actually bind)",
  heloan30Row38CltvAnchor:
    "Keep 30-yr HELOAN 620-639 pricing anchored to the ≤60 CLTV column (workbook copy-fill quirk)",
  gfcTypoZeroesHeloan5yrIo8590:
    "Keep the HELOAN 5-yr IO 85-90 CLTV rate adjustor zeroed (workbook typo)",
  bsNotOfferedAsNegativeAdjustor:
    "Export Bank-Statement 'not offered' cells the workbook way (as negative rate adjustors) instead of the engine's -99 sentinel",
  buydownSpreadLiteral015:
    "Force the 0.15 buydown spread regardless of the knob above (the workbook hardcodes it)",
  copy800RowsFrom780:
    "Price FICO 800+ rows as copies of 780-799 (workbook behavior)",
  refRate300Copies360:
    "Use the 360-month reference rate for 300-month terms (workbook behavior)",
};

// Adjuster rows that do NOT price today's quotes — verified against the engine
// source. Shown muted with the reason, so nobody tunes a dead dial expecting a
// pricing change.
const INERT_ADJUSTERS = {
  Balance0: "engine applies this ONLY to origination dates Feb 18–23 2025 (historical repricing) — editing won't change today's quotes",
  Balance100: "engine applies this ONLY to origination dates Feb 18–23 2025 (historical repricing) — editing won't change today's quotes",
  Balance200: "engine applies this ONLY to origination dates Feb 18–23 2025 (historical repricing) — editing won't change today's quotes",
  Balance350: "not exported to any engine file — editing does nothing",
  "2+ Borrowers": "not exported to any engine file — editing does nothing (reserved placeholder)",
  "Self-Employed": "not exported to any engine file — editing does nothing",
};

const REF_LABELS = [
  ["apor10yr", "APOR 10yr"], ["apor15yr", "APOR 15yr"], ["apor20yr", "APOR 20yr"], ["apor30yr", "APOR 30yr"],
  ["prime", "Prime rate"],
  ["treasury10yr", "Treasury 10yr"], ["treasury15yr", "Treasury 15yr"], ["treasury20yr", "Treasury 20yr"], ["treasury30yr", "Treasury 30yr"],
  ["hpThreshold", "HPML threshold spread"], ["hcThreshold", "High-cost threshold spread"],
  ["capMargin", "Coupon-cap margin"], ["tnRate", "TN max rate"],
];

function Num({ value, onChange, width = 78 }) {
  // While focused, show the raw keystrokes (so "-", "0.", "1.2500" all type
  // naturally); while blurred, show the noise-stripped value. Editing still
  // emits real numbers, so the stored doc stays numeric and untouched values
  // keep their exact stored precision — this is display-only cleanup.
  const [text, setText] = useState(null);
  const display = fmtNum(value);
  return (
    <input
      type="text"
      inputMode="decimal"
      className="ke-num"
      style={{ width }}
      value={text === null ? display : text}
      onFocus={() => setText(display)}
      onBlur={() => setText(null)}
      onChange={(e) => {
        const t = e.target.value;
        setText(t);
        if (t.trim() === "") {
          onChange(null);
          return;
        }
        const n = Number(t);
        if (isFinite(n)) onChange(n); // ignore partial input like "-" or "1."
      }}
    />
  );
}

function MatrixTable({ rowLabels, colLabels, matrix, onCell, reverse, rowNotes }) {
  const safeMatrix = Array.isArray(matrix) ? matrix : [];
  const safeCols = Array.isArray(colLabels) ? colLabels : [];
  const safeRows = Array.isArray(rowLabels) ? rowLabels : [];
  // Optionally present rows top-to-bottom in reverse (e.g. lowest FICO on top)
  // WITHOUT reordering the stored arrays the derivation reads positionally:
  // the true data index drives both the row label and the write-back.
  const order = safeMatrix.map(function (_, p) {
    return reverse ? safeMatrix.length - 1 - p : p;
  });
  return (
    <div className="ke-scroll">
      <table className="ke-table">
        <thead>
          <tr>
            <th />
            {safeCols.map((c, j) => (
              <th key={j}>{String(c)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {order.map((i) => {
            const note = rowNotes && rowNotes[i];
            return (
              <tr key={i} className={note ? "ke-inert" : undefined} title={note || undefined}>
                <th>
                  {String(safeRows[i] ?? i + 1)}
                  {note && <span className="ke-inertmark"> ⚠ inert</span>}
                </th>
                {(Array.isArray(safeMatrix[i]) ? safeMatrix[i] : []).map((v, j) => (
                  <td key={j}>
                    <Num value={v} onChange={(nv) => onCell(i, j, nv)} width={70} />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function KnobEditor({ doc, onChange }) {
  const [rawText, setRawText] = useState(null);
  if (!doc || typeof doc !== "object") return null;
  const knobs = doc.knobs || {};
  const refRates = knobs.refRates || {};
  const llpa = doc.llpa || {};
  const offsets = doc.offsets || {};
  const flags = knobs.flags || {};
  const ph = knobs.placeholders || {};

  const set = (fn) => {
    const next = deep(doc);
    fn(next);
    onChange(next);
  };
  // Path-safe writers: a first-boot or hand-edited document may lack the
  // intermediate objects, and an edit must never throw.
  const setKnob = (key, v) => set((d) => { d.knobs = d.knobs || {}; d.knobs[key] = v; });
  const setRef = (key, v) => set((d) => { d.knobs = d.knobs || {}; d.knobs.refRates = d.knobs.refRates || {}; d.knobs.refRates[key] = v; });
  const setFlag = (key, v) => set((d) => { d.knobs = d.knobs || {}; d.knobs.flags = d.knobs.flags || {}; d.knobs.flags[key] = v; });
  // Adjusters with malformed entries render read-only labels but never crash.
  const adjusters = Array.isArray(llpa.adjusters)
    ? llpa.adjusters.map((a, idx) => ({ a, idx })).filter((x) => x.a && Array.isArray(x.a.values))
    : [];

  return (
    <div className="ke-wrap">
      <div className="ke-intro">
        <b>Everything below sets PRICE (in points), not the note rate directly.</b>{" "}
        The engine derives the borrower's rate by finding the rung on the rate↔price
        ladder whose price matches — so a price LLPA of −0.25 nudges the rate slightly
        up. Six of these (Cash Out, Investor, Second Home, 2–4 Unit, HELOC, long terms)
        are <i>also</i> converted to a direct rate bump for the qualifying-rate path —
        the derivation computes that from the same price value, so you only tune it once
        here. Adjustments that hit the rate <i>only after</i> pricing — state surcharges
        (TX/TN/IA), partner rate bumps, draw-term discounts — live in the engine code,
        not in these knobs. <b>DTI and DTI&nbsp;Low are pure price LLPAs</b> (no separate
        rate effect), so they behave exactly like the credit panel.
      </div>
      <div className="ke-section">
        <div className="ke-h">Pricing</div>
        <div className="ke-fieldrow">
          <label>
            Base sale price (px)
            <Num value={knobs.basePrice} onChange={(v) => setKnob("basePrice", v)} />
          </label>
          <label>
            Extra margin (px points)
            <Num value={knobs.priceMargin} onChange={(v) => setKnob("priceMargin", v)} />
          </label>
          <label>
            Buydown spread (points)
            <Num value={knobs.buydownSpread} onChange={(v) => setKnob("buydownSpread", v)} />
          </label>
        </div>
        <div className="ke-hint">
          Base price is the target sale price of the 20-yr HELOAN — the root of the whole
          stack. Raising margin lowers borrower rates; the buydown spread is the extra
          points charged per rate buydown.
        </div>
      </div>

      <details className="ke-section" open>
        <summary className="ke-h">Reference rates (update weekly)</summary>
        <div className="ke-fieldrow">
          {REF_LABELS.map(([key, label]) => (
            <label key={key}>
              {label}
              <Num value={refRates[key]} onChange={(v) => setRef(key, v)} />
            </label>
          ))}
        </div>
      </details>

      <details className="ke-section">
        <summary className="ke-h">Credit LLPAs — PRICE adjustment by FICO × CLTV (from the GSMC panel)</summary>
        {Array.isArray(llpa.creditPanel) && (
          <MatrixTable
            rowLabels={llpa.creditFicoTiers || []}
            colLabels={llpa.cltvColumns || []}
            matrix={llpa.creditPanel}
            reverse
            onCell={(i, j, v) => set((d) => { d.llpa.creditPanel[i][j] = v; })}
          />
        )}
        <div className="ke-hint">
          Blank = not quoted by GSMC (no credit adjustment for eligible bands). The
          low-FICO overlays are shown and editable here directly — e.g. <b>620–639
          gets −3.5 at ≤60% CLTV and −4.0 at 60–65%</b>; edit those cells to re-tune the
          620 dial. (Nothing is hidden in Advanced anymore.)
        </div>
      </details>

      <details className="ke-section">
        <summary className="ke-h">Product & feature LLPAs — PRICE adjustments (IO, HELOC, terms, occupancy…)</summary>
        {adjusters.length > 0 && (
          <MatrixTable
            rowLabels={adjusters.map((x) => (x.a && x.a.name) || "row " + (x.idx + 1))}
            colLabels={llpa.cltvColumns || []}
            matrix={adjusters.map((x) => x.a.values)}
            rowNotes={adjusters.map((x) => INERT_ADJUSTERS[x.a && x.a.name] || null)}
            onCell={(i, j, v) => set((d) => { d.llpa.adjusters[adjusters[i].idx].values[j] = v; })}
          />
        )}
        <div className="ke-hint">
          Rows marked <b>⚠ inert</b> do not price today's quotes (hover for why) —
          the Balance tiers only ever applied to one week of Feb-2025 originations,
          and Balance350 / 2+ Borrowers / Self-Employed are not exported to the
          engine at all. Re-activating any of them is an engine change.
        </div>
      </details>

      <details className="ke-section">
        <summary className="ke-h">HELOAN price offsets (hand-tuned, by FICO × CLTV)</summary>
        {Array.isArray(offsets.heloan) && (
          <MatrixTable
            rowLabels={offsets.ficoTiers || []}
            colLabels={llpa.cltvColumns || []}
            matrix={offsets.heloan}
            onCell={(i, j, v) => set((d) => { d.offsets.heloan[i][j] = v; })}
          />
        )}
        <div className="ke-hint">These are the cells you re-tune most weeks. Blank = no offset.</div>
      </details>

      <details className="ke-section">
        <summary className="ke-h">HELOC price offsets</summary>
        {Array.isArray(offsets.heloc) && (
          <MatrixTable
            rowLabels={offsets.ficoTiers || []}
            colLabels={llpa.cltvColumns || []}
            matrix={offsets.heloc}
            onCell={(i, j, v) => set((d) => { d.offsets.heloc[i][j] = v; })}
          />
        )}
      </details>

      <details className="ke-section">
        <summary className="ke-h">HELOAN Bank Statement price offsets</summary>
        {Array.isArray(offsets.bankStatement) && (
          <MatrixTable
            rowLabels={offsets.ficoTiers || []}
            colLabels={llpa.cltvColumns || []}
            matrix={offsets.bankStatement}
            onCell={(i, j, v) => set((d) => { d.offsets.bankStatement[i][j] = v; })}
          />
        )}
        <div className="ke-hint">
          Blank = Bank Statement not offered in that cell (exported per the
          workbook-quirk flag below). Values are price offsets on the BS grid.
        </div>
      </details>

      <div className="ke-section">
        <div className="ke-h">Reserved pricing slots (not yet active)</div>
        <div className="ke-placeholder">
          <label className="ke-locked">
            <input type="checkbox" checked={!!(ph.coBorrowerLlpa && ph.coBorrowerLlpa.enabled)} disabled />
            Co-borrower pricing adjustment — reserved. Turning this on requires engine-side
            work (roadmap Stage 3/5); the derivation refuses to run with it enabled.
          </label>
          <label className="ke-locked">
            <input type="checkbox" checked={!!(ph.dtiLlpa && ph.dtiLlpa.enabled)} disabled />
            Tiered DTI pricing (multiple bands above 43%) — reserved for the GOLD 50 work.
            The simple over/under-43 dials are live today: edit the <b>DTI</b> row
            (cost above 43) and the <b>DTI Low</b> row (credit at/under 43) in the
            Product &amp; feature LLPAs table above. Don't enable this flag — the
            derivation refuses to run with it on.
          </label>
        </div>
      </div>

      <details className="ke-section">
        <summary className="ke-h">Workbook quirks (ticked = replicate the workbook exactly)</summary>
        {Object.keys(FLAG_LABELS).map((k) => (
          <label key={k} className="ke-flag">
            <input
              type="checkbox"
              checked={flags[k] !== false}
              onChange={(e) => setFlag(k, e.target.checked)}
            />
            {FLAG_LABELS[k]}
          </label>
        ))}
        <div className="ke-hint">
          Untick only as a deliberate pricing decision — each change shows up in the
          preview diff before anything publishes.
        </div>
      </details>

      <details className="ke-section">
        <summary className="ke-h">Advanced — full document (JSON)</summary>
        {/* Stays live-synced with the structured editor unless the user is
            actively typing here (rawText null = mirror the doc). */}
        <textarea
          className="form-control ra-knobs"
          rows={12}
          value={rawText === null ? JSON.stringify(doc, null, 1) : rawText}
          onChange={(e) => setRawText(e.target.value)}
        />
        <div className="ke-fieldrow">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              try {
                const k = JSON.parse(rawText === null ? JSON.stringify(doc) : rawText);
                if (!k || typeof k !== "object" || Array.isArray(k)) throw new Error("must be a JSON object");
                setRawText(null);
                onChange(k);
              } catch (e) {
                window.alert("JSON doesn't parse: " + e.message);
              }
            }}
          >
            Apply JSON to editor
          </button>
          {rawText !== null && (
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setRawText(null)}>
              Discard JSON edits
            </button>
          )}
        </div>
      </details>
    </div>
  );
}

export default KnobEditor;
